import { apiRequest } from '../../../shared/api';
import {
  Risk,
  RiskMatrix,
  RiskDashboardStats,
  RiskStatus,
  RiskTolerability,
  RiskAssessment,
  RiskControl,
  RiskReview,
  RiskHazardLink,
} from './types';

export interface RiskListResponse {
  items: Risk[];
  total: number;
}

export const riskService = {
  // Matrix & Heatmap
  getDefaultMatrix: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<RiskMatrix>(`/risks/matrix/default?organizationId=${organizationId}`),

  getHeatmapData: (organizationId: string = 'ORG-XYZ', useResidual: boolean = false) =>
    apiRequest<{ matrix: RiskMatrix; distribution: Record<string, number> }>(
      `/risks/matrix/heatmap?organizationId=${organizationId}&useResidual=${useResidual}`
    ),

  // Dashboard Stats
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<RiskDashboardStats>(`/risks/analytics/dashboard?organizationId=${organizationId}`),

  // Risk List
  getRisks: (params: {
    organizationId?: string;
    status?: RiskStatus | RiskStatus[];
    tolerability?: RiskTolerability;
    hazardId?: string;
    ownerId?: string;
    sourceModule?: string;
    sourceId?: string;
    searchTerm?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach((s) => query.append('status', s));
      } else {
        query.append('status', params.status);
      }
    }
    if (params.tolerability) query.append('tolerability', params.tolerability);
    if (params.hazardId) query.append('hazardId', params.hazardId);
    if (params.ownerId) query.append('ownerId', params.ownerId);
    if (params.sourceModule) query.append('sourceModule', params.sourceModule);
    if (params.sourceId) query.append('sourceId', params.sourceId);
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    return apiRequest<RiskListResponse>(`/risks?${query.toString()}`);
  },

  // Get Detail
  getRiskById: (id: string) => apiRequest<Risk>(`/risks/${id}`),

  // Create
  createRisk: (data: Partial<Risk>) =>
    apiRequest<Risk>('/risks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update
  updateRisk: (id: string, data: Partial<Risk>) =>
    apiRequest<Risk>(`/risks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Transition Status
  transitionStatus: (id: string, toStatus: RiskStatus, reason: string, changedBy?: string) =>
    apiRequest<Risk>(`/risks/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason, changedBy }),
    }),

  // Assessments
  addAssessment: (
    id: string,
    assessmentType: string,
    severity: number,
    likelihood: string,
    justification: string,
    assessorId?: string,
    assessorName?: string
  ) =>
    apiRequest<RiskAssessment>(`/risks/${id}/assessments`, {
      method: 'POST',
      body: JSON.stringify({
        assessmentType,
        severity,
        likelihood,
        justification,
        assessorId,
        assessorName,
      }),
    }),

  // Controls & Mitigations
  addControl: (
    id: string,
    title: string,
    controlType: string,
    description: string,
    effectivenessScore?: number,
    ownerDepartment?: string,
    actionReferenceId?: string,
    threatId?: string,
    consequenceId?: string
  ) =>
    apiRequest<RiskControl>(`/risks/${id}/controls`, {
      method: 'POST',
      body: JSON.stringify({
        title,
        controlType,
        description,
        effectivenessScore,
        ownerDepartment,
        actionReferenceId,
        threatId,
        consequenceId,
      }),
    }),

  updateControlStatus: (id: string, controlId: string, status: string, effectivenessScore?: number) =>
    apiRequest<{ success: boolean }>(`/risks/${id}/controls/${controlId}`, {
      method: 'PUT',
      body: JSON.stringify({ status, effectivenessScore }),
    }),

  removeControl: (id: string, controlId: string) =>
    apiRequest<{ success: boolean }>(`/risks/${id}/controls/${controlId}`, {
      method: 'DELETE',
    }),

  // Reviews & Management Acceptance
  recordReview: (
    id: string,
    reviewerId: string,
    reviewerName: string,
    reviewType: string,
    verdict: string,
    notes: string,
    customNextReviewDays?: number
  ) =>
    apiRequest<RiskReview>(`/risks/${id}/reviews`, {
      method: 'POST',
      body: JSON.stringify({
        reviewerId,
        reviewerName,
        reviewType,
        verdict,
        notes,
        customNextReviewDays,
      }),
    }),

  // Hazard Links
  linkHazard: (id: string, hazardId: string, relationshipNote?: string) =>
    apiRequest<RiskHazardLink>(`/risks/${id}/hazards`, {
      method: 'POST',
      body: JSON.stringify({ hazardId, relationshipNote }),
    }),

  unlinkHazard: (id: string, hazardId: string) =>
    apiRequest<{ success: boolean }>(`/risks/${id}/hazards/${hazardId}`, {
      method: 'DELETE',
    }),

  // Comments
  addComment: (id: string, commentText: string, userId?: string, userName?: string) =>
    apiRequest<any>(`/risks/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ commentText, userId, userName }),
    }),

  // Attachments
  addAttachment: (
    id: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    storagePath: string,
    uploadedBy?: string
  ) =>
    apiRequest<any>(`/risks/${id}/attachments`, {
      method: 'POST',
      body: JSON.stringify({ fileName, fileType, fileSize, storagePath, uploadedBy }),
    }),

  // Bowtie Diagram Integration (Visual & Tabular Dual Sync)
  getBowtieDiagram: (id: string) =>
    apiRequest<import('./bowtie/bowtie.types').BowtieDiagram>(`/risks/${id}/bowtie`),

  addBowtieElement: (
    id: string,
    payload: {
      type: 'threat' | 'preventionBarrier' | 'mitigationBarrier' | 'consequence';
      title: string;
      description?: string;
      effectivenessScore?: number;
      ownerDepartment?: string;
      threatId?: string;
      consequenceId?: string;
    }
  ) =>
    apiRequest<import('./bowtie/bowtie.types').BowtieDiagram>(`/risks/${id}/bowtie/elements`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateBowtieBarrier: (
    id: string,
    barrierId: string,
    payload: {
      title?: string;
      status?: string;
      effectivenessScore?: number;
      barrierStatus?: 'ok' | 'weak' | 'failed';
      threatId?: string | null;
      consequenceId?: string | null;
    }
  ) =>
    apiRequest<import('./bowtie/bowtie.types').BowtieDiagram>(`/risks/${id}/bowtie/barriers/${barrierId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteBowtieBarrier: (id: string, barrierId: string) =>
    apiRequest<import('./bowtie/bowtie.types').BowtieDiagram>(`/risks/${id}/bowtie/barriers/${barrierId}`, {
      method: 'DELETE',
    }),

  deleteBowtieThreat: (id: string, causeId: string) =>
    apiRequest<import('./bowtie/bowtie.types').BowtieDiagram>(`/risks/${id}/bowtie/threats/${causeId}`, {
      method: 'DELETE',
    }),

  // SafetyManager365 Risk+ & RMM Parity APIs
  evaluateChecklist: (payload: { tier4_entity_id?: number; answers: Array<{ question_id: number; answer: boolean }> }) =>
    apiRequest<any>('/v1/riskplus/checklist/evaluate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getRiskplusAssessments: (params?: Record<string, any>) => {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ items: any[]; total: number }>(`/v1/riskplus/assessments${q}`);
  },

  createRiskplusAssessment: (payload: any) =>
    apiRequest<any>('/v1/riskplus/assessments', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getRiskplusRegister: (params?: Record<string, any>) => {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ total: number; page: number; limit: number; items: any[] }>(`/v1/riskplus/register${q}`);
  },

  getRiskRegisterGroups: () =>
    apiRequest<any[]>('/v1/rmm/risk-register/groups'),

  getRiskOverviewBoard: (params?: Record<string, any>) => {
    const q = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiRequest<{ last_updated_utc: string; items: any[] }>(`/v1/rmm/overview-board${q}`);
  },

  syncRiskOverviewBoard: () =>
    apiRequest<any>('/v1/rmm/overview-board/sync', {
      method: 'POST',
    }),
};
